import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { RestaurantService } from '../../../services/restaurant/restaurant.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import {FileUploadService} from '../../../services/file-upload.service';
import {switchMap} from 'rxjs/operators';

type AddTableForm = {
  name: FormControl<string>;
  widgetCode: FormControl<string>;
  dwallDeviceId: FormControl<string>;
  description: FormControl<string>;
  QRCodeFile: FormControl<File | null>;
  number: FormControl<number| null>;
};

@UntilDestroy()
@Component({
  selector: 'app-add-table',
  templateUrl: './add-table.component.html',
  styleUrls: ['./add-table.component.scss']
})
export class AddTableComponent implements OnInit {

  public addTable: FormGroup<AddTableForm> = this.generateForm();
  public qrFile: File | null = null;
  public restaurantId: any;
  public language = localStorage.getItem('lang') ?? this.translate.currentLang;
  public isUploading = false;
  public connectionType: 'widgetCode' | 'dwallDeviceId' = 'widgetCode';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private translate: TranslateService,
    private restaurantService: RestaurantService,
    public dialogRef: MatDialogRef<any>,
    private fileUpload: FileUploadService
  ) {
  }

  ngOnInit(): void {
    this.restaurantId = this.data?.restaurantId;
    this.updateConnectionValidators();
  }

  addNewTable(form: FormGroup<AddTableForm>): void {
    this.dialogRef.disableClose = true;
    this.isUploading = true;
    const table = this.getTablePayload(form);

    if (this.qrFile) {
      this.fileUpload.upload(this.qrFile).pipe(
        switchMap(event => {
          table.qrCodeFileId = event.intent.fileId.toString();
          return this.restaurantService.addTable(this.restaurantId, table);
        })
      ).subscribe((data) => {
        this.isUploading = false;
        this.dialogRef.close(data);
      }, () => {
        this.onSaveError();
      });
    } else {
      this.restaurantService.addTable(this.restaurantId, table).subscribe((data) => {
        this.isUploading = false;
        this.dialogRef.close(data);
      }, () => {
        this.onSaveError();
      });
    }
  }

  private generateForm(): FormGroup<AddTableForm> {
    return new FormGroup({
      name: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      widgetCode: new FormControl('', { nonNullable: true }),
      dwallDeviceId: new FormControl('', { nonNullable: true, validators: [Validators.pattern(/^\d{12}$/)] }),
      description: new FormControl('', { nonNullable: true }),
      QRCodeFile: new FormControl<File | null>(null),
      number: new FormControl<number | null>(null, {validators: [Validators.pattern(/^\d+$/), Validators.min(0)] }),
    });
  }

  public onConnectionTypeChange(index: number): void {
    this.connectionType = index === 0 ? 'widgetCode' : 'dwallDeviceId';
    this.updateConnectionValidators();
  }

  private updateConnectionValidators(): void {
    const { widgetCode: widgetCodeControl, dwallDeviceId: dwallDeviceIdControl } = this.addTable.controls;

    if (this.connectionType === 'widgetCode') {
      widgetCodeControl?.setValidators([Validators.required]);
      dwallDeviceIdControl?.clearValidators();
    } else {
      widgetCodeControl?.clearValidators();
      dwallDeviceIdControl?.setValidators([Validators.required, Validators.pattern(/^\d{12}$/)]);
    }

    widgetCodeControl?.updateValueAndValidity();
    dwallDeviceIdControl?.updateValueAndValidity();
  }

  private getTablePayload(form: FormGroup<AddTableForm>): {
    name: string,
    number?: number,
    widgetCode?: string,
    dwallDeviceId?: string,
    description?: string,
    qrCodeFileId?: string,
  } {
    const value = form.getRawValue();
    const table: {
      name: string,
      number?: number,
      widgetCode?: string,
      dwallDeviceId?: string,
      description?: string,
      qrCodeFileId?: string,
    } = {
      name: value.name,
      description: value.description,
    };

    if (value.number) {
      table.number = Number(value.number);
    }

    if (this.connectionType === 'widgetCode') {
      table.widgetCode = value.widgetCode.trim();
    } else {
      table.dwallDeviceId = value.dwallDeviceId.trim();
    }

    return table;
  }

  private onSaveError(): void {
    this.isUploading = false;
    this.dialogRef.disableClose = false;
  }

  onQrFileChange($event: Event): void {
    const file = ($event.target as HTMLInputElement).files;
    if (file) {
      this.qrFile = file[0];
      this.addTable.patchValue({ QRCodeFile: file[0] });
    }
  }
}
